import { registerSW } from 'virtual:pwa-register'

export interface PwaState {
  needRefresh: boolean
  offlineReady: boolean
  update: () => Promise<void>
}

type Listener = (s: PwaState) => void
const listeners = new Set<Listener>()
let state: PwaState = { needRefresh: false, offlineReady: false, update: async () => {} }

function emit() {
  for (const l of listeners) l(state)
}

export function initPwa() {
  if (!('serviceWorker' in navigator)) return
  const update = registerSW({
    immediate: true,
    onNeedRefresh() {
      state = { ...state, needRefresh: true, update: () => update(true) }
      emit()
    },
    onOfflineReady() {
      state = { ...state, offlineReady: true }
      emit()
    },
  })
  state = { ...state, update: () => update(true) }
}

export function subscribePwa(l: Listener) {
  listeners.add(l)
  l(state)
  return () => {
    listeners.delete(l)
  }
}

/** Captures the browser's install prompt so the UI can offer a custom install button. */
let deferredPrompt:
  | (Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> })
  | null = null
const installListeners = new Set<(available: boolean) => void>()

export function initInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredPrompt = e as typeof deferredPrompt
    for (const l of installListeners) l(true)
  })
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null
    for (const l of installListeners) l(false)
  })
}

export function subscribeInstall(l: (available: boolean) => void) {
  installListeners.add(l)
  l(deferredPrompt !== null)
  return () => {
    installListeners.delete(l)
  }
}

export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable'
  await deferredPrompt.prompt()
  const { outcome } = await deferredPrompt.userChoice
  deferredPrompt = null
  return outcome === 'accepted' ? 'accepted' : 'dismissed'
}

export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true
  )
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent)
}
